import { Injectable, Injector } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CriticalUpdateDialogComponent } from '../app/apps/shared/critical-update-dialog/critical-update-dialog.component';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import { environment } from '../environments/environment';

type PlatformKey = 'ios' | 'android';
@Injectable({ providedIn: 'root' })
export class UpdateCheckService {
  dialogRef?: MatDialogRef<CriticalUpdateDialogComponent>;
  public hasShownCriticalUpdate = false;
  public hasShownNonCriticalUpdate = false;
  public isCriticalUpdateAvaliable = false;
  public showAlert = false;
  private _dialog?: MatDialog;

  constructor(private injector: Injector) {}

  private get dialog(): MatDialog {
    if (!this._dialog) {
      this._dialog = this.injector.get(MatDialog);
    }
    return this._dialog;
  }

  private isNewer(latest: string, current: string): boolean {
    const l = (latest || '0').split('.').map(n => +n || 0);
    const c = (current || '0').split('.').map(n => +n || 0);

    // Compare major version first
    if (l[0] > c[0]) return true;
    if (l[0] < c[0]) return false;

    // If major is same, compare minor version
    if ((l[1] || 0) > (c[1] || 0)) return true;
    if ((l[1] || 0) < (c[1] || 0)) return false;

    // If major and minor are same, compare patch version
    return (l[2] || 0) > (c[2] || 0);
  }

  /**
   * Call on cold launch + on resume.
   * Always shows a NON-DISMISSABLE modal if an update is available.
   * Returns true if the modal was shown.
   */
  async checkForUpdate(): Promise<boolean | null> {
    if (!Capacitor.isNativePlatform()) return null;

    const platform = Capacitor.getPlatform() as PlatformKey;
    const { version: currentVersion, build: _ } = await App.getInfo();

    // 2) Fetch version info and store URLs from server's version.json
    let storeUrlFromServer = '';
    let versionFromServer = '';
    let useTestVersion = false;
    try {
      const versionJsonUrl = `${environment.baseUrl}assets/version/version.json?t=${Date.now()}`;

      // Add timeout to prevent hanging on captive portal networks
      // 5 seconds is sufficient for normal networks while preventing captive portal hang
      // If this times out, we fall back to store API check which has its own timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(versionJsonUrl, { signal: controller.signal });
      clearTimeout(timeoutId);

      // Check if response is actually JSON (not captive portal HTML)
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.warn('[UpdateCheck] Received non-JSON response (likely captive portal) - skipping');
        throw new Error('Non-JSON response');
      }

      const versionData = await response.json();
      storeUrlFromServer = versionData?.storeUrl?.[platform] || '';
      versionFromServer = versionData?.NativeVersion_Test || '';
      useTestVersion = versionData?.UseTestVersion === true;
    } catch (error: any) {
      // Log error for debugging but don't block app - will fall back to plugin openAppStore
      if (error.name === 'AbortError') {
        console.warn('[UpdateCheck] Fetch timeout (likely slow network or captive portal) - will use store API fallback');
      } else {
        console.warn('[UpdateCheck] Version fetch failed:', error.message);
      }
      // ignore; we'll fall back to plugin openAppStore
    }

    // 1) Store-aware check (preferred)
    let storeSaysUpdate = false;

    // If UseTestVersion flag is set in version.json, use NativeVersion_Test for testing
    // Otherwise, use actual store check
    if (useTestVersion && versionFromServer) {
      // Test mode: Compare current version with NativeVersion_Test from version.json
      storeSaysUpdate = this.isNewer(versionFromServer, currentVersion);
    } else {
      // Production: Query actual app store
      try {
        const info = await AppUpdate.getAppUpdateInfo();
        if (platform === 'android') {
          storeSaysUpdate = info.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE;
        } else {
          const available = (info as any).availableVersionName as string | undefined;
          storeSaysUpdate = !!available && this.isNewer(available, currentVersion);
        }
      } catch {
        // ignore; we'll fall back to server policy below
      }
    }

    const shouldPrompt = storeSaysUpdate;

    // If modal is already showing but update is no longer needed, close it
    if (this.dialogRef && !shouldPrompt) {
      this.dialogRef.close(false);
      this.dialogRef = undefined;
      this.hasShownCriticalUpdate = false;
      this.hasShownNonCriticalUpdate = false;
      this.isCriticalUpdateAvaliable = false;
      this.showAlert = false;
      return false;
    }

    // If modal is already showing and update is still needed, keep it open
    if (this.dialogRef) return true;

    if (!shouldPrompt) return false;

    // Set flags IMMEDIATELY before showing modal to prevent race conditions
    // This ensures HomeComponent (or any other component) checking these flags
    // will see them as true before navigation can occur
    this.hasShownCriticalUpdate = true;
    this.hasShownNonCriticalUpdate = true;
    this.isCriticalUpdateAvaliable = true;
    this.showAlert = true;

    await this.showForcedUpdate(storeUrlFromServer);

    return true;
  }

  private async showForcedUpdate(storeUrl: string) {
    // already showing? don't spawn another
    if (this.dialogRef) return;

    this.dialogRef = this.dialog.open(CriticalUpdateDialogComponent, {
      disableClose: true,  // NON-DISMISSABLE - prevents backdrop click and ESC key
      data: {
        storeUrl: storeUrl,
        onOpenStore: async () => {
          if (storeUrl) {
            await Browser.open({
              url: storeUrl,
              presentationStyle: 'fullscreen',
              windowName: '_system'
            });
          } else {
            // fallback: plugin can open the correct store listing
            await AppUpdate.openAppStore();
          }
        }
      }
    });

    // when dialog closes, clear the reference
    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = undefined;
    });
  }
}
